import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Heart, AlertCircle, Send, Loader2, Shield, Users, Sparkles, Wifi } from './icons/CustomDashboardIcons';
import { toast } from 'sonner';
import { HeartAlchemyIcon } from './icons/AlchemyIcons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const URGENCY_OPTIONS = [
  { value: 'low', label: 'Low - When you have time', color: 'bg-blue-500' },
  { value: 'normal', label: 'Normal - Within a day', color: 'bg-amber-500' },
  { value: 'high', label: 'High - Within hours', color: 'bg-orange-500' },
  { value: 'urgent', label: 'Urgent - As soon as possible', color: 'bg-red-500' }
];

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General Support', icon: '💭' },
  { value: 'family', label: 'Family Situation', icon: '👨‍👩‍👧‍👦' },
  { value: 'relationship', label: 'Relationship', icon: '💑' },
  { value: 'work', label: 'Work/Career', icon: '💼' },
  { value: 'self', label: 'Self-Connection', icon: '🧘' },
  { value: 'grief', label: 'Grief/Loss', icon: '🕯️' },
  { value: 'anxiety', label: 'Anxiety/Stress', icon: '😰' }
];

export default function EmpathyAlertButton({ variant = 'default', className = '' }) {
  const [showDialog, setShowDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    urgency: 'normal',
    category: 'general',
    anonymous: false
  });

  const userId = localStorage.getItem('workbook_user_id');
  const userName = localStorage.getItem('workbook_user_name');

  const handleSubmit = async () => {
    if (!userId) {
      toast.error('Please log in to send an empathy alert');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('Please provide a brief description of your situation');
      return;
    }

    setSending(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/empathy-alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
          'X-User-Name': userName || 'Anonymous'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        
        // Trigger WebSocket broadcast for real-time notification
        try {
          await fetch(`${BACKEND_URL}/api/ws/empathy-alert`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId
            },
            body: JSON.stringify({
              title: formData.title,
              message: formData.description || 'Someone needs empathetic support',
              urgency: formData.urgency,
              category: formData.category
            })
          });
          console.log('[EmpathyAlert] WebSocket broadcast triggered');
        } catch (wsError) {
          console.log('[EmpathyAlert] WebSocket broadcast failed, alert still saved:', wsError);
        }
        
        setSent(true);
        toast.success('Your empathy request has been sent to the community! 💚');
        
        // Reset after showing success
        setTimeout(() => {
          setShowDialog(false);
          setSent(false);
          setFormData({
            title: '',
            description: '',
            urgency: 'normal',
            category: 'general',
            anonymous: false
          });
        }, 3000);
      } else {
        throw new Error('Failed to send alert');
      }
    } catch (error) {
      toast.error('Failed to send empathy alert. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const buttonVariants = {
    default: 'bg-gradient-to-r from-rose-600 to-amber-600 hover:from-rose-500 hover:to-amber-500 text-white shadow-lg',
    compact: 'bg-rose-600 hover:bg-rose-500 text-white',
    footer: 'bg-transparent border border-rose-500/50 text-rose-400 hover:bg-rose-500/10 hover:border-rose-400',
    ghost: 'bg-transparent text-rose-500 hover:bg-rose-500/10'
  };

  return (
    <>
      <Button
        onClick={() => setShowDialog(true)}
        className={`${buttonVariants[variant]} ${className}`}
        data-testid="empathy-alert-btn"
      >
        <HeartAlchemyIcon className="w-4 h-4 mr-2" />
        {variant === 'compact' ? 'SOS' : 'Instant Empathy'}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-lg mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl font-light flex items-center gap-3 text-amber-300" style={{ fontFamily: 'Playfair Display, serif' }}>
              <HeartAlchemyIcon className="w-6 h-6 text-rose-400" />
              Instant Empathy Request
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              Reach out to our community of empathetic listeners. Someone will connect with you soon.
            </DialogDescription>
          </DialogHeader>

          {sent ? (
            <div className="py-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-rose-500/20 to-amber-500/20 flex items-center justify-center mx-auto">
                <Heart className="w-8 h-8 text-rose-400 animate-pulse" />
              </div>
              <h3 className="text-lg font-medium text-white">Request Sent 💚</h3>
              <p className="text-stone-400 text-sm">
                Your request has been shared with our community of empathetic listeners.
                Someone will reach out to you soon through messages.
              </p>
              <Badge className="bg-teal-500/20 text-teal-400 border-teal-500/30">
                Check your Dashboard for responses
              </Badge>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              {/* Title/Brief Description */}
              <div>
                <Label className="text-stone-300 text-sm mb-2 block">
                  What's happening? <span className="text-rose-400">*</span>
                </Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Brief description of your situation..."
                  className="bg-stone-800 border-stone-700 text-white"
                  data-testid="empathy-alert-title"
                />
              </div>

              {/* More Details */}
              <div>
                <Label className="text-stone-300 text-sm mb-2 block">
                  More details (optional)
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Share as much or as little as you'd like..."
                  className="bg-stone-800 border-stone-700 text-white min-h-[80px]"
                  data-testid="empathy-alert-description"
                />
              </div>

              {/* Category */}
              <div>
                <Label className="text-stone-300 text-sm mb-2 block">Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {CATEGORY_OPTIONS.map(cat => (
                      <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-stone-700">
                        <span className="mr-2">{cat.icon}</span> {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Urgency */}
              <div>
                <Label className="text-stone-300 text-sm mb-2 block">How soon do you need support?</Label>
                <Select 
                  value={formData.urgency} 
                  onValueChange={(value) => setFormData({ ...formData, urgency: value })}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {URGENCY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-stone-700">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${opt.color}`} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Anonymous Option */}
              <div className="flex items-start gap-3 p-3 bg-stone-800/50 rounded-lg border border-stone-700">
                <Checkbox
                  id="anonymous"
                  checked={formData.anonymous}
                  onCheckedChange={(checked) => setFormData({ ...formData, anonymous: checked })}
                  className="mt-0.5"
                />
                <div>
                  <Label htmlFor="anonymous" className="text-stone-300 text-sm cursor-pointer">
                    Request anonymously
                  </Label>
                  <p className="text-xs text-stone-500 mt-1">
                    Your name will be hidden. Responders will only see "Someone in need"
                  </p>
                </div>
              </div>

              {/* Privacy Notice */}
              <div className="flex items-start gap-2 p-3 bg-teal-500/10 rounded-lg border border-teal-500/20">
                <Shield className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-teal-300">
                  Your request will only be visible to verified Belonging Sessions members. 
                  All conversations are private and confidential.
                </p>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={sending || !formData.title.trim()}
                className="w-full bg-gradient-to-r from-rose-600 to-amber-600 hover:from-rose-500 hover:to-amber-500 text-white"
                data-testid="empathy-alert-submit"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending Request...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Empathy Request
                  </>
                )}
              </Button>

              {/* Community Note */}
              <p className="text-xs text-stone-500 text-center">
                <Users className="w-3 h-3 inline mr-1" />
                Our community of trained empathetic listeners is here for you
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
